import { c as _c } from "react/compiler-runtime";
import AutosuggestComponent, { type SuggestionSource } from '../autosuggest/autosuggest.component';
import React, { ChangeEvent, FormEvent, ReactElement, useCallback, useEffect, useRef } from 'react';
import useDropdownHandler, { DropdownResult } from '../../lib/react/hooks/useDropdownHandler';
import ImageComponent from '@spa/components/image-component/image.component';
import { NamespacedIntl } from '@spa/lib/i18n/common/NamespacedIntl';
import { SiteLinksSearchBoxJsonLd } from 'next-seo';
import classNames from 'clsx';
import { decodeUrlParamSafely } from '@spa/lib/decodeUrlParamSafely';
import selectPublicBaseUrl from '../../lib/store/settings/selectors/selectPublicBaseUrl';
import selectSearchSettings from '@spa/lib/store/settings/selectors/selectSearchSettings';
import serializePath from '@spa/lib/router/serializePath';
import styles from './search-bar.component.module.scss';
import useNamespacedIntl from '@spa/lib/i18n/client/hooks/useNamespacedIntl';
import useObjectReducer from '@spa/lib/react/hooks/useObjectReducer';
import { useRouter } from 'next/navigation';
import useRuntimeSearchParams from '@spa/lib/react/hooks/useRuntimeSearchParams';
import { useSelector } from 'react-redux';
const DROPDOWN_CLOSE_DELAY_MS = 200;
export interface Props {
  readonly id: string;
}
export default function SearchBarComponent(t0) {
  const $ = _c(89);
  const {
    id
  } = t0;
  const router = useRouter();
  const searchParams = useRuntimeSearchParams();
  const qParam = decodeUrlParamSafely(searchParams.get("q")?.trim() ?? "");
  const [state, updateState] = useObjectReducer({
    inputValue: qParam,
    prevQParam: qParam,
    selectedIndex: -1,
    suggestions: [] as Array<SuggestionSource>
  });
  const {
    formatMessage
  } = useNamespacedIntl("search-bar");
  const publicBaseUrl = useSelector(selectPublicBaseUrl);
  const {
    vertexSearchEnabled
  } = useSelector(selectSearchSettings);
  const {
    dropdownRef,
    isDropdownOpen,
    setIsDropdownOpen
  } = useDropdownHandler(`${id}-dropdown`, state.suggestions.length);
  const inputRef = useRef(null);
  const {
    inputValue,
    prevQParam,
    selectedIndex,
    suggestions
  } = state;
  const listboxId = `${id}-autosuggest-listbox`;
  if (qParam !== prevQParam) {
    updateState({
      inputValue: qParam,
      prevQParam: qParam,
      selectedIndex: -1
    });
  }
  let t1;
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      const shouldRestoreFocus = sessionStorage.getItem("restoreSearchFocus");
      if (shouldRestoreFocus === "true" && inputRef.current) {
        inputRef.current?.focus();
        sessionStorage.removeItem("restoreSearchFocus");
      }
    };
    t2 = [inputRef];
    $[0] = t1;
    $[1] = t2;
  } else {
    t1 = $[0];
    t2 = $[1];
  }
  useEffect(t1, t2);
  let t3;
  if ($[2] !== inputValue || $[3] !== setIsDropdownOpen) {
    t3 = () => {
      if (inputValue.trim()) {
        setIsDropdownOpen(true);
      }
    };
    $[2] = inputValue;
    $[3] = setIsDropdownOpen;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onClickHandler = t3;
  let t4;
  if ($[5] !== setIsDropdownOpen || $[6] !== updateState) {
    t4 = () => {
      setTimeout(() => {
        setIsDropdownOpen(false);
        updateState({
          selectedIndex: -1
        });
      }, DROPDOWN_CLOSE_DELAY_MS);
    };
    $[5] = setIsDropdownOpen;
    $[6] = updateState;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const onBlurHandler = t4;
  let t5;
  if ($[8] !== setIsDropdownOpen || $[9] !== updateState) {
    t5 = event => {
      const newValue = event.target.value;
      updateState({
        inputValue: newValue,
        selectedIndex: -1
      });
      setIsDropdownOpen(!!newValue.trim());
    };
    $[8] = setIsDropdownOpen;
    $[9] = updateState;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  const onChangeHandler = t5;
  let t6;
  if ($[11] !== inputValue || $[12] !== isDropdownOpen || $[13] !== router || $[14] !== selectedIndex || $[15] !== setIsDropdownOpen || $[16] !== suggestions || $[17] !== updateState || $[18] !== vertexSearchEnabled) {
    t6 = event_0 => {
      const renderedSuggestionsCount = suggestions.length;
      if (event_0.key === "Tab" && isDropdownOpen) {
        setIsDropdownOpen(false);
        updateState({
          selectedIndex: -1
        });
      }
      if (!isDropdownOpen || renderedSuggestionsCount === 0) {
        if (event_0.key === "Escape") {
          setIsDropdownOpen(false);
        }
        return;
      }
      bb50: switch (event_0.key) {
        case "ArrowDown":
          {
            event_0.preventDefault();
            updateState(prev_0 => ({
              selectedIndex: prev_0.selectedIndex < renderedSuggestionsCount - 1 ? prev_0.selectedIndex + 1 : prev_0.selectedIndex
            }));
            break bb50;
          }
        case "ArrowUp":
          {
            event_0.preventDefault();
            updateState(_temp);
            break bb50;
          }
        case "Escape":
          {
            event_0.preventDefault();
            setIsDropdownOpen(false);
            updateState({
              selectedIndex: -1
            });
            break bb50;
          }
        case "Enter":
          {
            if (selectedIndex >= 0 && suggestions[selectedIndex]) {
              event_0.preventDefault();
              const suggestion = suggestions[selectedIndex];
              if (typeof suggestion === "string") {
                sessionStorage.setItem("restoreSearchFocus", "true");
                const encodedQuery = encodeURIComponent(suggestion).replace(/%20/g, " ");
                const newSearchParams = new URLSearchParams();
                newSearchParams.set("q", encodedQuery);
                router.push(serializePath("/search/", newSearchParams));
              } else {
                router.push(suggestion.path);
              }
              setIsDropdownOpen(false);
              updateState({
                inputValue: typeof suggestion === "string" && vertexSearchEnabled ? suggestion : inputValue,
                selectedIndex: -1
              });
            }
          }
      }
    };
    $[11] = inputValue;
    $[12] = isDropdownOpen;
    $[13] = router;
    $[14] = selectedIndex;
    $[15] = setIsDropdownOpen;
    $[16] = suggestions;
    $[17] = updateState;
    $[18] = vertexSearchEnabled;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const onKeyDownHandler = t6;
  let t7;
  if ($[20] !== inputValue || $[21] !== router || $[22] !== setIsDropdownOpen || $[23] !== updateState) {
    t7 = event_1 => {
      event_1.preventDefault();
      const trimmedValue = inputValue.trim();
      if (trimmedValue) {
        const encodedQuery_0 = encodeURIComponent(trimmedValue).replace(/%20/g, " ");
        const newSearchParams_0 = new URLSearchParams();
        newSearchParams_0.set("q", encodedQuery_0);
        sessionStorage.setItem("restoreSearchFocus", "true");
        router.push(serializePath("/search/", newSearchParams_0));
        setIsDropdownOpen(false);
        updateState({
          selectedIndex: -1
        });
      }
    };
    $[20] = inputValue;
    $[21] = router;
    $[22] = setIsDropdownOpen;
    $[23] = updateState;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  const onSubmitHandler = t7;
  let t8;
  if ($[25] !== updateState) {
    t8 = nextSuggestions => {
      updateState({
        suggestions: nextSuggestions
      });
    };
    $[25] = updateState;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const handleSuggestionsUpdate = t8;
  let t9;
  if ($[27] !== updateState) {
    t9 = suggestion_0 => {
      updateState({
        inputValue: suggestion_0
      });
    };
    $[27] = updateState;
    $[28] = t9;
  } else {
    t9 = $[28];
  }
  const handleSuggestionSelect = t9;
  let t10;
  if ($[29] !== isDropdownOpen) {
    t10 = classNames(styles["search-bar"], {
      [styles["search-bar--focused"]]: isDropdownOpen
    });
    $[29] = isDropdownOpen;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const searchBarClass = t10;
  const t11 = `${publicBaseUrl}/search/?q`;
  let t12;
  if ($[31] !== t11) {
    t12 = [{
      target: t11,
      queryInput: "search_term_string"
    }];
    $[31] = t11;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== publicBaseUrl || $[34] !== t12) {
    t13 = <SiteLinksSearchBoxJsonLd useAppDir={true} url={publicBaseUrl} potentialActions={t12} />;
    $[33] = publicBaseUrl;
    $[34] = t12;
    $[35] = t13;
  } else {
    t13 = $[35];
  }
  let t14;
  if ($[36] !== setIsDropdownOpen) {
    t14 = () => setIsDropdownOpen(false);
    $[36] = setIsDropdownOpen;
    $[37] = t14;
  } else {
    t14 = $[37];
  }
  let t15;
  if ($[38] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <ImageComponent className={styles["search-bar__return-icon"]} src="/assets/icons/arrow_back.svg" alt="back icon" width={20} height={45} />;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== t14) {
    t16 = <button onClick={t14} className={styles["search-bar_return-button"]} type="button">{t15}</button>;
    $[39] = t14;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== formatMessage) {
    t17 = formatMessage("placeholder");
    $[41] = formatMessage;
    $[42] = t17;
  } else {
    t17 = $[42];
  }
  let t18;
  if ($[43] !== id || $[44] !== t17) {
    t18 = <label htmlFor={id} className="sr-only">{t17}</label>;
    $[43] = id;
    $[44] = t17;
    $[45] = t18;
  } else {
    t18 = $[45];
  }
  let t19;
  if ($[46] !== formatMessage) {
    t19 = formatMessage("placeholder");
    $[46] = formatMessage;
    $[47] = t19;
  } else {
    t19 = $[47];
  }
  const t20 = selectedIndex >= 0 ? `${listboxId}-option-${selectedIndex}` : undefined;
  let t21;
  if ($[48] !== id || $[49] !== inputValue || $[50] !== onBlurHandler || $[51] !== onChangeHandler || $[52] !== onClickHandler || $[53] !== onKeyDownHandler || $[54] !== t19 || $[55] !== t20) {
    t21 = <input id={id} ref={inputRef} name="q" className={styles["search-bar__input"]} placeholder={t19} onClick={onClickHandler} onBlur={onBlurHandler} value={inputValue} onChange={onChangeHandler} onKeyDown={onKeyDownHandler} autoComplete="off" type="search" aria-autocomplete="both" aria-activedescendant={t20} data-track-id="searchBox" />;
    $[48] = id;
    $[49] = inputValue;
    $[50] = onBlurHandler;
    $[51] = onChangeHandler;
    $[52] = onClickHandler;
    $[53] = onKeyDownHandler;
    $[54] = t19;
    $[55] = t20;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== formatMessage) {
    t22 = formatMessage("buttonTitle");
    $[57] = formatMessage;
    $[58] = t22;
  } else {
    t22 = $[58];
  }
  let t23;
  if ($[59] !== formatMessage) {
    t23 = formatMessage("buttonTitle");
    $[59] = formatMessage;
    $[60] = t23;
  } else {
    t23 = $[60];
  }
  const t24 = `${t23}`;
  let t25;
  if ($[61] !== t24) {
    t25 = <ImageComponent src="/assets/icons/search-white.svg" alt={t24} width={32} height={32} />;
    $[61] = t24;
    $[62] = t25;
  } else {
    t25 = $[62];
  }
  let t26;
  if ($[63] !== t22 || $[64] !== t25) {
    t26 = <button type="submit" className={styles["search-bar__icon"]} title={t22} tabIndex={0} data-track-id="searchButton">{t25}</button>;
    $[63] = t22;
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== setIsDropdownOpen || $[67] !== updateState) {
    t27 = () => {
      setIsDropdownOpen(false);
      updateState({
        selectedIndex: -1
      });
    };
    $[66] = setIsDropdownOpen;
    $[67] = updateState;
    $[68] = t27;
  } else {
    t27 = $[68];
  }
  let t28;
  if ($[69] !== handleSuggestionSelect || $[70] !== handleSuggestionsUpdate || $[71] !== inputValue || $[72] !== isDropdownOpen || $[73] !== listboxId || $[74] !== selectedIndex || $[75] !== t27) {
    t28 = <AutosuggestComponent isInputFocused={isDropdownOpen} listboxId={listboxId} onClickOnChildElement={t27} queryStr={inputValue} pageIdx={0} pageSize={20} selectedIndex={selectedIndex} onSuggestionSelect={handleSuggestionSelect} onSuggestionsUpdate={handleSuggestionsUpdate} />;
    $[69] = handleSuggestionSelect;
    $[70] = handleSuggestionsUpdate;
    $[71] = inputValue;
    $[72] = isDropdownOpen;
    $[73] = listboxId;
    $[74] = selectedIndex;
    $[75] = t27;
    $[76] = t28;
  } else {
    t28 = $[76];
  }
  let t29;
  if ($[77] !== dropdownRef || $[78] !== t16 || $[79] !== t18 || $[80] !== t21 || $[81] !== t26 || $[82] !== t28) {
    t29 = <div ref={dropdownRef} className={styles["search-bar__wrapper"]}>{t16}{t18}{t21}{t26}{t28}</div>;
    $[77] = dropdownRef;
    $[78] = t16;
    $[79] = t18;
    $[80] = t21;
    $[81] = t26;
    $[82] = t28;
    $[83] = t29;
  } else {
    t29 = $[83];
  }
  let t30;
  if ($[84] !== onSubmitHandler || $[85] !== searchBarClass || $[86] !== t13 || $[87] !== t29) {
    t30 = <form className={searchBarClass} onSubmit={onSubmitHandler} action="/search/" role="search" aria-label="Search site">{t13}{t29}</form>;
    $[84] = onSubmitHandler;
    $[85] = searchBarClass;
    $[86] = t13;
    $[87] = t29;
    $[88] = t30;
  } else {
    t30 = $[88];
  }
  return t30;
}
function _temp(prev) {
  return {
    selectedIndex: prev.selectedIndex > -1 ? prev.selectedIndex - 1 : -1
  };
}